import axios from "axios";
import * as AuthService from "./AuthService";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { toast } from "react-toastify";
import { isValid, format, parse } from "date-fns";
import React from "react";
import formStyles from "../../assets/scss/common/formStyle.module.scss";


/* ---------- dayjs setup ---------- */

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

/* ---------- Types ---------- */

export interface CurrentUser {
  userid?: number;
  username?: string;

  paginationlength?: number;
  userdisplayname?: string;
  versionid?: number;
  versionname?: string;
  iscompanysettingrr?: boolean;
  [key: string]: unknown;
}

type ToastType = "success" | "error";

/* ---------- Local Storage ---------- */

export const clearLocalStorage = (): void => {
  const baseUrl = localStorage.getItem("baseUrl");
  const filebaseUrl = localStorage.getItem("filebaseUrl");
  const reportUrl = localStorage.getItem("reportUrl");
  const iPAddress = localStorage.getItem("iPAddress");
  const versionName = localStorage.getItem("VersionName");
  const tab = localStorage.getItem("REMS_Tab");
  const isIdealTimeOut = sessionStorage.getItem("IsIdealTimeOut");

  localStorage.removeItem("activestudyid")
  localStorage.removeItem("activevolunteer")
  localStorage.removeItem("activevolunteerid")
  localStorage.removeItem("volunteerName")
  localStorage.removeItem("currentPage")
  localStorage.removeItem("currentUser")
  localStorage.removeItem("token")
  localStorage.removeItem("studycode")
  localStorage.removeItem("role")
  localStorage.removeItem("returnForm")
  localStorage.removeItem("volunteername")
  localStorage.removeItem("volunteerassignmentid")
  localStorage.removeItem("globalstudyname")
  localStorage.removeItem("globalstudyid")
  localStorage.removeItem("active_volunteer_screening_status")

  // Signal StudyProvider to reset in-memory selectedStudy → null immediately
  window.dispatchEvent(new Event("clearGlobalStudy"));

  // localStorage.clear();

  if (baseUrl) localStorage.setItem("baseUrl", baseUrl);
  if (filebaseUrl) localStorage.setItem("filebaseUrl", filebaseUrl);
  if (reportUrl) localStorage.setItem("reportUrl", reportUrl);
  if (iPAddress) localStorage.setItem("iPAddress", iPAddress);
  if (versionName) localStorage.setItem("VersionName", versionName);
  if (tab) localStorage.setItem("REMS_Tab", tab);
  if (isIdealTimeOut)
    sessionStorage.setItem("IsIdealTimeOut", isIdealTimeOut);
};


/* ---------- Auth ---------- */

export const userLogout = (navigate: (path: string) => void): void => {
  const currentUser = localStorage.getItem("currentUser");

  if (currentUser) {
    const parsed: CurrentUser = JSON.parse(currentUser);
    AuthService.userLogout(String(parsed.username)).then(() => {
      navigate("/");
    });
  } else {
    toast.error("Something wrong!", { autoClose: 3000 });
  }
};

export const GetUserData = (): CurrentUser | null => {
  const user = localStorage.getItem("currentUser");
  return user ? (JSON.parse(user) as CurrentUser) : null;
};

export const GetSystemSettingValue = (settingNameOrId: string | number): string | null => {
  const user = GetUserData();
  if (!user || !Array.isArray(user.settings)) return null;

  const searchStr = String(settingNameOrId).toLowerCase();

  let setting = user.settings.find(
    (s: any) =>
      String(s.settingmasterid) === String(settingNameOrId) ||
      (s.settingname && s.settingname.toLowerCase() === searchStr)
  );

  if (!setting) {
    setting = user.settings.find(
      (s: any) => s.settingname && s.settingname.toLowerCase().includes(searchStr)
    );
  }

  return setting ? setting.settingvalue : null;
};

/* ---------- Toast ---------- */

export const showToastMessage = (
  type: ToastType,
  message: string
): void => {
  if (type === "success") toast.success(message, { autoClose: 2000 });
  else toast.error(message, { autoClose: 2000 });
};





//--------------------------------------------------------------------------------------
//--> Normalize From Value
//--------------------------------------------------------------------------------------



/* ---------- Pagination ---------- */

export const perPageData: number =
  GetUserData()?.paginationlength ?? 10;

export const refreshPagination = <T>(
  pageData: T[],
  currentPage: number
): T[] => {
  const indexOfLast = currentPage * perPageData;
  const indexOfFirst = indexOfLast - perPageData;
  return pageData?.slice(indexOfFirst, indexOfLast);
};

/* ---------- Date Helpers ---------- */

export const dateFormatOptions = {
  dateFormat: "Y-m-d",
  altFormat: "Y-m-d",
  altInput: true,
};


export const DateFormat = (
  date: Date | string | null
): string | null => {
  if (!date) return null;

  // already Date object
  if (date instanceof Date) {
    return isValid(date)
      ? format(date, "yyyy-MM-dd")
      : null;
  }

  // string input
  if (typeof date === "string") {

    // if already ISO like 2026-12-21
    let parsed = parse(date, "yyyy-MM-dd", new Date());

    // fallback dd-MM-yyyy like 21-12-2026
    if (!isValid(parsed)) {
      parsed = parse(date, "dd-MM-yyyy", new Date());
    }

    if (!isValid(parsed)) return null;

    return format(parsed, "yyyy-MM-dd");
  }

  return null;
};


export const pastAndCurrentDatesOnly = {
  dateFormat: "Y-m-d",
  altFormat: "Y-m-d",
  altInput: true,
  maxDate: "today",
};
export const formatDateSplit = (date: any) => {
  if (!date) return null;

  if (date instanceof Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  if (typeof date === "string") {
    return date.includes("T") ? date.split("T")[0] : date;
  }

  return null;
};

/* ---------- Sorting ---------- */

let sortBy = false;

export const setOrder = <T,>(
  data: T[],
  value: keyof T,
  setSelectAllData?: React.Dispatch<React.SetStateAction<T[]>>
): void => {
  if (data && data.length > 0) {
    sortBy = !sortBy;
    // sorting logic intentionally commented
  }
};

/* ---------- Formik Helper ---------- */

export const handleChange = (
  formik: { handleChange: (e: React.ChangeEvent<any>) => void },
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
): void => {
  const { value } = e.target;

  if (value.length === 1 && value === "\n") return;
  if (value.trim() === "" && value.includes(" ")) return;

  formik.handleChange(e);
};



// export const parseLocalDate = (value: Date | string | null | undefined): Date | null => {
//   if (!value) return null;
//   if (value instanceof Date) {
//     return isNaN(value.getTime()) ? null : value;
//   }
//   if (typeof value === "string") {
//     const cleanStr = value.trim();
//     // YYYY-MM-DD
//     const yyyymmddMatch = cleanStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
//     if (yyyymmddMatch) {
//       const parsed = dayjs(yyyymmddMatch[0], "YYYY-MM-DD");
//       return parsed.isValid() ? parsed.toDate() : null;
//     }
//     // DD-MM-YYYY
//     const ddmmyyyyMatch = cleanStr.match(/^(\d{2})-(\d{2})-(\d{4})/);
//     if (ddmmyyyyMatch) {
//       const parsed = dayjs(ddmmyyyyMatch[0], "DD-MM-YYYY");
//       return parsed.isValid() ? parsed.toDate() : null;
//     }
//     const parsed = dayjs(cleanStr);
//     return parsed.isValid() ? parsed.toDate() : null;
//   }
//   return null;
// };

export const parseLocalDate = (
  value: Date | string | null | undefined
): Date | null => {
  if (!value) return null;

  // Date object
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  const cleanStr = value.trim();

  if (!cleanStr) return null;

  // --------------------------------------------------
  // 1. Datetime with timezone
  // Example:
  // 2026-08-27T08:23:55.435522Z
  // 2026-09-01T04:26:01.805128+00:00
  // 2026-09-01 04:26:01.805128+00
  // --------------------------------------------------
  const hasTimeZone =
    /^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2}|[+-]\d{2})$/.test(
      cleanStr
    );

  if (hasTimeZone) {
    // PostgreSQL may return +00 instead of +00:00
    const normalized = cleanStr.replace(
      /([+-]\d{2})$/,
      "$1:00"
    );

    const parsed = new Date(normalized);

    return isNaN(parsed.getTime()) ? null : parsed;
  }

  // --------------------------------------------------
  // 2. YYYY-MM-DD
  // This is a date WITHOUT timezone.
  // Keep it as a local date.
  // --------------------------------------------------
  if (/^\d{4}-\d{2}-\d{2}$/.test(cleanStr)) {
    const parsed = dayjs(cleanStr, "YYYY-MM-DD", true);

    return parsed.isValid() ? parsed.toDate() : null;
  }

  // --------------------------------------------------
  // 3. DD-MM-YYYY
  // --------------------------------------------------
  if (/^\d{2}-\d{2}-\d{4}$/.test(cleanStr)) {
    const parsed = dayjs(cleanStr, "DD-MM-YYYY", true);

    return parsed.isValid() ? parsed.toDate() : null;
  }

  // --------------------------------------------------
  // 4. Fallback
  // --------------------------------------------------
  const parsed = dayjs(cleanStr);

  return parsed.isValid() ? parsed.toDate() : null;
};


export const formatDate = (value?: Date | string) => {
  if (!value) return "-";
  const d = parseLocalDate(value);

  if (!d || isNaN(d.getTime())) return "-";

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();

  return `${dd}-${mm}-${yyyy}`;
};

/** Centralized 24-hour time conversion helper function */
export const formatTime24Hour = (timeStr?: string | null, includeSeconds: boolean = true): string => {
  if (!timeStr) return "-";
  const cleanStr = String(timeStr).trim();
  if (cleanStr === "" || cleanStr === "--" || cleanStr === "-") return "-";

  // Check if it has AM/PM (12-hour format)
  const ampmMatch = cleanStr.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (ampmMatch) {
    let [_, hStr, mStr, sStr, ampm] = ampmMatch;
    let h = parseInt(hStr, 10);
    const m = mStr;
    const s = sStr || "00";
    const isPM = ampm.toUpperCase() === "PM";
    const isAM = ampm.toUpperCase() === "AM";

    if (isPM && h < 12) h += 12;
    if (isAM && h === 12) h = 0;

    const hh = String(h).padStart(2, "0");
    return includeSeconds ? `${hh}:${m}:${s}` : `${hh}:${m}`;
  }

  // Check if already in 24-hour format "HH:mm:ss" or "HH:mm"
  const time24Match = cleanStr.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (time24Match) {
    const [_, hStr, mStr, sStr] = time24Match;
    const hh = String(parseInt(hStr, 10)).padStart(2, "0");
    const m = mStr;
    const s = sStr || "00";
    return includeSeconds ? `${hh}:${m}:${s}` : `${hh}:${m}`;
  }

  // Fallback: parse as Date
  try {
    const date = new Date(cleanStr);
    if (!isNaN(date.getTime())) {
      const hh = String(date.getHours()).padStart(2, "0");
      const mm = String(date.getMinutes()).padStart(2, "0");
      const ss = String(date.getSeconds()).padStart(2, "0");
      return includeSeconds ? `${hh}:${mm}:${ss}` : `${hh}:${mm}`;
    }
  } catch (e) {
    // ignore
  }

  return cleanStr;
};

/** Get current system time in 24-hour format ("HH:mm:ss" or "HH:mm") */
export const getNow24Hour = (includeSeconds: boolean = true): string => {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  if (!includeSeconds) return `${hh}:${mm}`;
  const ss = String(now.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
};